"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Link as LinkIcon, Share2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import Icon from "@/components/Icons/Icon";
import IconFacebook from "@/components/Icons/IconFacebook";
import IconLinkedin from "@/components/Icons/IconLinkedin";
import IconTwitter from "@/components/Icons/IconTwitter";
import IconWhatsapp from "@/components/Icons/IconWhatsapp";
import { copyToClipboard, generateSocialShareLinks, SocialShareOptions } from "@/utils/socialShare";

interface SocialSharePopupProps {
  options: SocialShareOptions;
  className?: string;
}

const SocialSharePopup: React.FC<SocialSharePopupProps> = ({ options, className = "" }) => {
  const locale = useLocale();
  const t = useTranslations("Utils");
  const [isCopied, setIsCopied] = useState(false);
  const isRTL = locale === "ar";
  const [shareLinks, setShareLinks] = useState<ReturnType<typeof generateSocialShareLinks> | null>(null);

  // Generate share links on the client side to avoid hydration issues
  useEffect(() => {
    setShareLinks(generateSocialShareLinks(options));
  }, [options]);

  const handleCopyLink = async () => {
    if (typeof navigator === "undefined") return;

    const success = await copyToClipboard(options.url);
    if (success) {
      setIsCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setIsCopied(false), 2000);
    } else {
      toast.error("Failed to copy link");
    }
  };

  const openShareWindow = (url: string) => {
    if (typeof window === "undefined") return;
    window.open(url, "share-dialog", "width=800,height=600,location=yes,resizable=yes,scrollbars=yes");
  };

  // If shareLinks is null, show a loading state or return early
  if (!shareLinks) {
    return (
      <div className={`inline-flex ${className}`}>
        <button
          className="items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm"
          aria-label="Share this article"
          disabled
        >
          <Share2 className="p-1" />
        </button>
      </div>
    );
  }

  return (
    <div className={`inline-flex ${className}`}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className="items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none"
            aria-label={t("socialShare")}
          >
            <Share2 className="p-1" />
            {/* <span>Share</span> */}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-[8000] min-w-[220px] rounded-2xl bg-white p-2 shadow-lg data-[side=bottom]:animate-slideDown"
            sideOffset={8}
            align="end"
            side="bottom"
            collisionPadding={10}
          >
            <div dir={isRTL ? "rtl" : "ltr"}>
              <div className="px-2 py-1.5">
                <h3 className="urbanist text-start text-sm font-medium text-gray-500">{t("socialShare")}</h3>
              </div>

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 rtl:flex-row"
                onClick={() => openShareWindow(shareLinks.twitter)}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <Icon
                    icon={<IconTwitter fill="black" height={20} width={20} />}
                    width={12}
                    height={12}
                    className="cursor-pointer rounded-full"
                  />
                </div>
                <span className="inter text-sm font-medium text-gray-700">{t("twitter")}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 rtl:flex-row"
                onClick={() => openShareWindow(shareLinks.facebook)}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <Icon
                    icon={<IconFacebook fill="black" />}
                    width={12}
                    height={12}
                    className="cursor-pointer rounded-full"
                  />
                </div>
                <span className="inter text-sm font-medium text-gray-700">{t("facebook")}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 rtl:flex-row"
                onClick={() => openShareWindow(shareLinks.linkedin)}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <Icon
                    icon={<IconLinkedin fill="black" />}
                    width={12}
                    height={12}
                    className="cursor-pointer rounded-full"
                  />
                </div>
                <span className="inter text-sm font-medium text-gray-700">{t("linkedin")}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 rtl:flex-row"
                onClick={() => openShareWindow(shareLinks.whatsapp)}
              >
                <div className="flex h-5 w-5 items-center justify-center">
                  <Icon icon={<IconWhatsapp fill="black" />} width={12} height={12} className="rounded-full" />
                </div>
                <span className="inter text-sm font-medium text-gray-700">{t("whatsapp")}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 rtl:flex-row"
                onClick={handleCopyLink}
              >
                <LinkIcon className={`h-5 w-5 ${isCopied ? "text-primary" : "text-gray-500"}`} />
                <span className="inter text-sm font-medium text-gray-700">
                  {isCopied ? t("copied") : t("copyLink")}
                </span>
              </DropdownMenu.Item>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export default SocialSharePopup;
